import { useEffect, useId, useRef, useState } from "react";

import { ArrowRight, Building2, Loader2, UserRound } from "lucide-react";

import { useMutation } from "@redwoodjs/web";

import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { marketingIntegrationTypes } from "src/lib/constants";
import { INTEGRATION_URL_MUTATION } from "src/lib/queries/integrations";
import { cn } from "src/lib/utils";

const connectionOptions = [
  {
    value: "BUSINESS",
    title: "Business portfolio",
    description:
      "For ad accounts managed in a Meta business portfolio. Best for ongoing access.",
    icon: Building2,
  },
  {
    value: "PERSONAL",
    title: "Personal account",
    description:
      "For ad accounts outside a business portfolio. Requires periodic reconnection.",
    icon: UserRound,
  },
];

/** A shared choice step for new connections and reconnecting an existing account. */
export function MetaConnectionDialog({
  open,
  onOpenChange,
  parentId,
  reconnectIntegrationId,
}) {
  const [mode, setMode] = useState("BUSINESS");
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const triggerRef = useRef(null);
  const id = useId();
  const [createIntegrationUrl, { loading }] = useMutation(
    INTEGRATION_URL_MUTATION
  );
  const busy = loading || redirecting;
  const MetaIcon = marketingIntegrationTypes.meta_ads.icon;

  useEffect(() => {
    if (open) {
      setMode("BUSINESS");
      setError(null);
      setRedirecting(false);
    }
  }, [open, parentId, reconnectIntegrationId]);

  const onContinue = async () => {
    if (busy) return;
    setError(null);
    try {
      const result = await createIntegrationUrl({
        variables: {
          input: {
            type: "META_ADS",
            parentId,
            metaLoginMode: mode,
            ...(reconnectIntegrationId ? { reconnectIntegrationId } : {}),
          },
        },
      });
      if (result.errors?.length) throw result.errors[0];
      if (!result.data?.integrationUrl)
        throw new Error(
          "Could not start the Meta connection. Please try again."
        );
      setRedirecting(true);
      window.location.assign(result.data.integrationUrl);
    } catch (error) {
      setRedirecting(false);
      setError(error.message || "Could not connect to Meta. Please try again.");
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy) onOpenChange(nextOpen);
      }}
    >
      <ResponsiveDialogContent
        className="gap-5"
        desktopClassName="max-h-[calc(100dvh-2rem)] sm:max-w-[500px]"
        mobileClassName="data-[vaul-drawer-direction=bottom]:max-h-[90dvh] overflow-hidden pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        onOpenAutoFocus={() => {
          triggerRef.current = document.activeElement;
        }}
        onCloseAutoFocus={(event) => {
          if (triggerRef.current?.isConnected) {
            event.preventDefault();
            triggerRef.current.focus();
          }
        }}
      >
        <div className="-m-1 min-h-0 space-y-5 overflow-y-auto p-1">
          <ResponsiveDialogHeader
            className="text-left"
            mobileClassName="gap-2 pb-0 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left"
          >
            <div className="mb-2 flex size-11 items-center justify-center rounded-xl border bg-muted/40">
              <MetaIcon className="h-5 w-7" aria-hidden="true" />
            </div>
            <ResponsiveDialogTitle>
              {reconnectIntegrationId
                ? "Reconnect Meta Ads"
                : "Connect Meta Ads"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription className="leading-relaxed">
              Choose how you manage your ad account. You’ll approve access on
              Meta next.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <RadioGroup
            aria-label="Meta connection type"
            value={mode}
            onValueChange={(value) => {
              setMode(value);
              setError(null);
            }}
            disabled={busy}
            className="gap-3"
          >
            {connectionOptions.map((option) => (
              <label
                key={option.value}
                htmlFor={`${id}-${option.value}`}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors focus-within:ring-2 focus-within:ring-ring/50",
                  mode === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/40",
                  busy && "pointer-events-none opacity-60"
                )}
              >
                <option.icon
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      id={`${id}-${option.value}-title`}
                      className="text-sm font-medium"
                    >
                      {option.title}
                    </span>
                    {option.value === "BUSINESS" && (
                      <Badge
                        variant="secondary"
                        className="px-1.5 py-0 text-[10px] font-medium"
                      >
                        Recommended
                      </Badge>
                    )}
                  </span>
                  <span
                    id={`${id}-${option.value}-description`}
                    className="mt-1 block text-sm leading-relaxed text-muted-foreground"
                  >
                    {option.description}
                  </span>
                </span>
                <RadioGroupItem
                  id={`${id}-${option.value}`}
                  value={option.value}
                  aria-labelledby={`${id}-${option.value}-title`}
                  aria-describedby={`${id}-${option.value}-description`}
                  className="mt-0.5 shrink-0"
                />
              </label>
            ))}
          </RadioGroup>

          {reconnectIntegrationId && (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Choose the same ad account on Meta to keep your sources and
              history connected.
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <ResponsiveDialogFooter className="shrink-0 border-t pt-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={onContinue} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {redirecting ? "Opening Meta…" : "Continue to Meta"}
            {!busy && <ArrowRight className="size-4" />}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
